import Vec2 from 'vec2';

import { isShapeAnIsland } from '@canvas-common';
import { AreaCalculationType, INSIDE_DIAGONAL_CORNER_MINIMUM_LENGTH, ISLAND_MINIMUM_AREA } from '@common/constants';
import { ShapeType, SideItemOption, SideItemType, containsBumpOut, containsRadiusOut } from '@common/enums';
import { MathFunctions, roundToArrowLengthGranularity } from '@common/helpers';
import { DrawingShape, SideItem } from '@common/models/drawing-shape.model';
import { getSideItemsFromShape } from '@common/utils/sideItems.utils';

import drawingTool from '../../pages/easy-estimator/components/scrollable-area/design-countertop/drawer/drawingTool';

/**
 * @type SideAreaAndLabor Consolidated calculated areas and edge length totals
 * @property totalWaterfallLaborEdgeLengths sum of all shape edge lengths for any sides that have waterfall edge selected
 * @property totalWaterfallArea sum of all shape waterfall edge panel areas
 * @property totalBacksplashArea sum of all shape backsplash areas
 * @property totalEndCapLaborEdgeCount total number of edges that require an endCap
 * @property totalEndSplashLaborEdgeCount total number of edges that require an endSplash
 */
export type SideAreaAndLabor = {
    totalWaterfallLaborEdgeLengths: number;
    totalWaterfallArea: number;
    totalBacksplashArea: number;
    totalEndCapLaborEdgeCount: number;
    totalEndSplashLaborEdgeCount: number;
};

/**
 * @param totalManufacturedArea this is the standard area plus diagonals, radius, bump-out **rounded up to nearest whole**
 * @param totalRawMaterialArea this is the raw materials (for wood) plus diagonals, radius, bump-out **rounded up to nearest whole**
 * @param manufacturedSlabArea the area of the manufactured slab only
 * @param rawMaterialSlabArea the area of the raw materials (wood, laminate) slab only
 * @param insideDiagonals area of all the inside diagonals
 * @param radiusOut area of all the radius out extra materials
 * @param bumpOut area of all the bump-out extra materials
 */
export interface ShapeAreas {
    manufacturedSlabArea: number;
    rawMaterialSlabArea: number;
    totalManufacturedArea: number;
    totalRawMaterialArea: number;
    insideDiagonals: number;
    radiusOut: number;
    bumpOut: number;
}

const NO_AREA = 0.0;
const NO_AREAS: ShapeAreas = {
    manufacturedSlabArea: NO_AREA,
    rawMaterialSlabArea: NO_AREA,
    totalManufacturedArea: NO_AREA,
    totalRawMaterialArea: NO_AREA,
    insideDiagonals: NO_AREA,
    radiusOut: NO_AREA,
    bumpOut: NO_AREA,
};

export const FEET_INCH_RATIO = 144; // number of square inches in a square foot
export const toSquareFeet = (squareInches: number): number => squareInches / FEET_INCH_RATIO;
export const toRoundedUpSquareFeet = (squareInches: number): number => Math.ceil(toSquareFeet(squareInches));

// eslint-disable-next-line sonarjs/cognitive-complexity
export const getShapeAreas = (shape: DrawingShape): ShapeAreas => {
    if (!shape || shape.skeleton.length === 0) {
        return NO_AREAS;
    }

    const sk = shape.skeleton;

    let manufacturedArea = NO_AREA;
    let rawMaterialArea = NO_AREA;
    let isLastRect = false; // Is used to check previous 2 segments are perpendicular

    for (let i = 1; i < sk?.length; i++) {
        const { prevUpperOutlinePt, nextUpperOutlinePt, prevBottomOutlinePt, nextBottomOutlinePt } =
            drawingTool.getOutlinePointsFromCorners([...Object.values(shape.cornerMap)], i);

        if (!(prevUpperOutlinePt && nextUpperOutlinePt && prevBottomOutlinePt && nextBottomOutlinePt)) {
            continue;
        }

        //Use get025Rounds func for the consistency with measurement text
        const upperDist = roundToArrowLengthGranularity(
            nextUpperOutlinePt.coords.subtract(prevUpperOutlinePt.coords, true).length()
        );
        const bottomDist = roundToArrowLengthGranularity(
            nextBottomOutlinePt.coords.subtract(prevBottomOutlinePt.coords, true).length()
        );

        const bigDist = Math.max(upperDist, bottomDist); //upperDist > bottomDist ? upperDist : bottomDist;
        const smallDist = Math.min(upperDist, bottomDist); //upperDist < bottomDist ? upperDist : bottomDist;
        const depth = sk[i - 1]?.depth * 2;

        const edgeLength = isLastRect ? smallDist : bigDist;
        const manufacturedSegmentArea = edgeLength * depth;
        manufacturedArea += manufacturedSegmentArea;

        const rawSegmentArea = Math.max(bigDist, smallDist) * depth;
        rawMaterialArea += rawSegmentArea;

        if (i === sk?.length - 1) {
            break;
        }

        const prevVec = new Vec2(sk[i]?.pos.x - sk[i - 1]?.pos.x, sk[i]?.pos.y - sk[i - 1]?.pos.y).normalize();
        const nextVec = new Vec2(sk[i + 1]?.pos.x - sk[i]?.pos.x, sk[i + 1]?.pos.y - sk[i]?.pos.y).normalize();

        if (prevVec.x * nextVec.x + prevVec.y * nextVec.y < Number.EPSILON) {
            if (isLastRect) {
                manufacturedArea += sk[i - 1]?.depth * sk[i]?.depth * 4;
            }
            isLastRect = true;
        } else {
            isLastRect = false;
        }
    }

    //  for extra elements such as inside diagonal, add the area of the extra points
    const insideDiagonals = calculateInsideDiagonalAreas(shape);

    // add area for radius outs
    const radiusOut = calculateRadiusOutAreas(shape);

    // add area for bump outs
    const bumpOut = calculateBumpOutAreas(shape);

    // make the totals
    const totalManufacturedArea = toRoundedUpSquareFeet(manufacturedArea + insideDiagonals + radiusOut + bumpOut);
    const totalRawMaterialArea = toRoundedUpSquareFeet(rawMaterialArea + insideDiagonals + radiusOut + bumpOut);

    return {
        totalManufacturedArea,
        totalRawMaterialArea,
        manufacturedSlabArea: toSquareFeet(manufacturedArea),
        rawMaterialSlabArea: toSquareFeet(rawMaterialArea),
        insideDiagonals: toSquareFeet(insideDiagonals),
        radiusOut: toSquareFeet(radiusOut),
        bumpOut: toSquareFeet(bumpOut),
    };
};

export const calculateInsideDiagonalAreas = (shape: DrawingShape): number => {
    return Object.values(shape.cornerMap).reduce((prev, item) => {
        if (
            item.diagonalLength !== undefined &&
            item.diagonalLength >= INSIDE_DIAGONAL_CORNER_MINIMUM_LENGTH &&
            item.replacementEndCoords?.x !== item.replacementStartCoords?.x &&
            item.replacementEndCoords?.y !== item.replacementStartCoords?.y &&
            item.angle &&
            item.angle < 0
        ) {
            const diagonalCornerArea = 0.5 * item.diagonalLength * item.diagonalLength;
            return prev + diagonalCornerArea;
        } else {
            return prev;
        }
    }, 0);
};

export const calculateRadiusOutAreas = (shape: DrawingShape): number => {
    return shape.drawingEdges
        .filter((edge) => containsRadiusOut(edge))
        .reduce((acc, edge) => {
            const arcWidth = MathFunctions.getDistanceBetween(
                shape.cornerMap[edge.startCornerId].coords,
                shape.cornerMap[edge.endCornerId].coords
            );
            const radius = MathFunctions.calculateRadiusFromArc(arcWidth, edge.radiusSagitta!);
            if (edge.radiusSagitta! > radius) {
                acc += edge.radiusSagitta! * radius * 2; // bigArc
            } else {
                acc += edge.radiusSagitta! * arcWidth; // littleArc
            }
            return acc;
        }, 0);
};

export const calculateBumpOutAreas = (shape: DrawingShape): number => {
    return shape.drawingEdges
        .filter((edge) => containsBumpOut(edge))
        .reduce(
            (acc, edge) =>
                acc +
                edge.bumpOut!.depth *
                    MathFunctions.getDistanceBetween(
                        shape.cornerMap[edge.startCornerId].coords,
                        shape.cornerMap[edge.endCornerId].coords
                    ),
            0
        );
};

export const getSideItemArea = (sideItem: SideItem) => {
    if (!sideItem?.arrowsInfo?.length) {
        return NO_AREA;
    }

    // get non-null/non-zero sizes
    const sizes: number[] = sideItem.arrowsInfo.map((arrow) => arrow.size ?? 0).filter((size) => !!size);
    const uniqueValues = sizes.filter((value, index, self) => self.indexOf(value) === index);

    if (uniqueValues && uniqueValues.length === 1) {
        return toRoundedUpSquareFeet(uniqueValues[0] * uniqueValues[0]);
    } else if (uniqueValues.length === 2) {
        return toRoundedUpSquareFeet(uniqueValues[0] * uniqueValues[1]);
    }
    return NO_AREA;
};

/**
 * calculate the area of a shape and return either the raw or manufactured value, based on whether the selectedMaterial uses manufactured or raw material calculations
 * @param shape the shape to calculate the area for
 * @param materialAreaType the type of area to calculate (raw or manufactured)
 * @see useShapeArea.tsx for selectedMaterialAreaType
 * @returns the area of the shape
 */
export const calculateBaseShapeArea = (shape: DrawingShape, materialAreaType: AreaCalculationType): number => {
    const shapeAreas = getShapeAreas(shape);
    return materialAreaType === AreaCalculationType.Raw
        ? shapeAreas.totalRawMaterialArea
        : shapeAreas.totalManufacturedArea;
};

/**
 * oversize island is a shape that is an island and area > ISLAND_MINIMUM_AREA
 * @param shape the shape to check
 * @param materialAreaType the type of area to calculate (raw or manufactured)
 * @returns true if the shape is an oversize island
 */
export const isShapeOversizeIsland = (shape: DrawingShape, materialAreaType: AreaCalculationType): boolean => {
    if (shape.type === ShapeType.FAUX_SHAPE) {
        return false;
    }
    return isShapeAnIsland(shape) && calculateBaseShapeArea(shape, materialAreaType) > ISLAND_MINIMUM_AREA;
};
const incrementIf = (value: number, condition: boolean | undefined, increment = 1) =>
    value + (condition ? increment : 0);

/**
 * Calculate the area of backsplashes and waterfalls plus labor quantities for different side items
 * @param drawingShapes the drawing shapes
 * @return totalBacksplashArea - total area of the backsplashes from all the shapes
 * @return totalWaterfallArea - total area of all the waterfall side panels from all the shapes
 * @return totalWaterfallLaborEdgeLengths - total linear inch of the edges with waterfall panels
 * @return totalEndCapLaborEdgeCount - total number of edges with end cap option
 * @return totalEndSplashLaborEdgeCount - total number of edges with an end splash option
 */
export const getAreaAndLaborQuantity = (drawingShapes: DrawingShape[]): SideAreaAndLabor => {
    const totalResults: SideAreaAndLabor = drawingShapes.reduce(
        (shapeAcc, shape) => {
            const allSideItemsForShape = getSideItemsFromShape(shape);
            const totalEndCapLaborEdgeCount = allSideItemsForShape.filter(
                (sideItem) => sideItem?.sideType === SideItemType.END_CAP
            ).length;
            const totalEndSplashLaborEdgeCount = allSideItemsForShape.filter(
                (sideItem) => sideItem?.sideType === SideItemType.END_SPLASH
            ).length;
            shapeAcc.totalEndCapLaborEdgeCount += totalEndCapLaborEdgeCount;
            shapeAcc.totalEndSplashLaborEdgeCount += totalEndSplashLaborEdgeCount;

            return shape.drawingEdges
                .filter(
                    (drawingEdge) =>
                        drawingEdge?.sideItem?.arrowsInfo &&
                        drawingEdge?.sideItem?.arrowsInfo?.length > 0 &&
                        (drawingEdge.sideItem?.type === SideItemOption.BACKSPLASH ||
                            drawingEdge.sideItem?.type === SideItemOption.WATERFALL)
                )
                .reduce((edgeAcc, edge) => {
                    /**
                     * Area of either the backsplash or waterfall, if there is one
                     */
                    const edgeSideItemArea = getSideItemArea(edge.sideItem);
                    /**
                     * Total edge length of all waterfall edges for this shape
                     */
                    const edgeWaterfallLaborQty = edge.sideItem.arrowsInfo
                        .filter((arrInf) => !arrInf.isSideItemPresent && arrInf.label !== '' && arrInf.size)
                        .reduce((qty, arrInf) => qty + (arrInf.size ?? 0), 0);
                    const totalBacksplashArea = incrementIf(
                        edgeAcc.totalBacksplashArea,
                        edge.sideItem.type === SideItemOption.BACKSPLASH,
                        edgeSideItemArea
                    );
                    const totalWaterfallArea = incrementIf(
                        edgeAcc.totalWaterfallArea,
                        edge.sideItem.type === SideItemOption.WATERFALL,
                        edgeSideItemArea
                    );
                    const totalWaterfallLaborEdgeLengths = incrementIf(
                        edgeAcc.totalWaterfallLaborEdgeLengths,
                        edge.sideItem.type === SideItemOption.WATERFALL,
                        edgeWaterfallLaborQty
                    );
                    return {
                        ...edgeAcc,
                        totalBacksplashArea,
                        totalWaterfallArea,
                        totalWaterfallLaborEdgeLengths,
                    };
                }, shapeAcc);
        },
        {
            totalWaterfallLaborEdgeLengths: 0.0,
            totalWaterfallArea: 0.0,
            totalBacksplashArea: 0.0,
            totalEndCapLaborEdgeCount: 0.0,
            totalEndSplashLaborEdgeCount: 0.0,
        } satisfies SideAreaAndLabor
    );

    return {
        ...totalResults,
        totalWaterfallLaborEdgeLengths: Math.ceil(totalResults.totalWaterfallLaborEdgeLengths),
    };
};
